import React from 'react';
import { Link } from 'react-router';
import { Box, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { ChevronLeft, FileText } from 'lucide-react';
import type { SnapshotFile } from 'src/api/dataVault/historyCertifications';

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------

export function formatDate(dateStr: string | null): string {
  if (!dateStr) {
    return '\u2014';
  }
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

export function formatUpdateDate(dateStr: string | null): string {
  if (!dateStr) {
    return '';
  }
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch {
    return dateStr;
  }
}

// ---------------------------------------------------------------------------
// FileStrip
// ---------------------------------------------------------------------------

export function FileStrip({
  files,
}: {
  files: SnapshotFile[];
}): React.ReactElement | null {
  const theme = useTheme();
  if (!files.length) {
    return null;
  }
  return (
    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
      {files.map((f) => (
        <Box
          key={f.id}
          component="a"
          href={f.download_url || '#'}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            bgcolor: alpha(theme.palette.error.main, 0.12),
            color: theme.palette.error.main,
            borderRadius: '6px',
            px: 1,
            py: 0.25,
            fontSize: 11,
            textDecoration: 'none',
            '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.2) },
          }}
        >
          <FileText size={12} />
          {f.filename || `File ${f.id}`}
        </Box>
      ))}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// BackLink
// ---------------------------------------------------------------------------

export function BackLink({ to }: { to: string }): React.ReactElement {
  return (
    <Box
      component={Link}
      to={to}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.25,
        color: 'primary.main',
        textDecoration: 'none',
        fontSize: 14,
        fontWeight: 500,
        lineHeight: 1,
      }}
    >
      <ChevronLeft size={18} style={{ position: 'relative', top: -1 }} />
      Back
    </Box>
  );
}

// ---------------------------------------------------------------------------
// CertHeader
// ---------------------------------------------------------------------------

export function CertHeader({
  propertyName,
  certTypeLabel,
  effectiveDate,
}: {
  propertyName: string;
  certTypeLabel: string | null;
  effectiveDate: string | null;
}): React.ReactElement {
  return (
    <Box sx={{ px: 2, pb: 2 }}>
      <Typography
        sx={{
          color: 'text.primary',
          fontSize: 20,
          fontWeight: 700,
        }}
      >
        {propertyName || '\u2014'}
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
        {certTypeLabel && (
          <Typography
            sx={{
              color: 'text.secondary',
              fontSize: 13,
            }}
          >
            {certTypeLabel}
          </Typography>
        )}
        {effectiveDate && (
          <Typography
            sx={{
              color: 'text.disabled',
              fontSize: 13,
            }}
          >
            &middot; {formatDate(effectiveDate)}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
